import { memo, useMemo } from 'react';
import { connect } from 'react-redux';

import useAuthRedirect from 'Containers/App/Layout/hooks/useAuthRedirect';
import {
  areEqual,
  getKeyName,
  getRegionConfig,
  mapDispatchToProps,
  mapStateToProps,
  type CommonProps,
} from 'Containers/App/Layout/utils';
import useShouldShowBanner from 'Containers/Banner/components/MarketingBanner/hooks/useShouldShowBanner';
import Loadable from 'Components/AsyncComponent';
import PageTitle from 'Components/PageTitle';
import PreRouteCheck from 'Components/Router/PreRouteCheck';

import Main from './Main';

const MarketingBanner = Loadable({
  loader: () =>
    import(
      'Containers/Banner/components/MarketingBanner' /* webpackChunkName: "MarketingBanner" */
    ),
  name: 'MarketingBanner',
});

const Mobile = (props: CommonProps) => {
  const {
    currentPath,
    hasPrevPath,
    initialized,
    loading,
    loggedIn,
    pathname,
    routeConfig,
    routeParams,
    search,
    setRouteEnd,
  } = props;

  useAuthRedirect({ search, routeConfig, initialized, loggedIn });

  const { mobileBottomNav, mobileTopNav, navbarPosition, pageMeta, showMobileBackground } =
    routeConfig ?? {};
  const main = getRegionConfig('main', routeConfig);
  const shouldShowWebDiscountBanner = useShouldShowBanner();

  const mainNode = useMemo(
    () => (
      <>
        {loggedIn && shouldShowWebDiscountBanner && <MarketingBanner />}
        <Main
          {...routeParams}
          showBackground={showMobileBackground}
          animation={main.animation}
          component={main.component}
          isDesktopCard={false}
          keyName={getKeyName(main.component, pathname)}
          navbarPosition={navbarPosition}
          onDidMount={setRouteEnd}
          pageProps={main.props}
          pathname={pathname}
          topNav={mobileTopNav}
          bottomNav={mobileBottomNav}
        />
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      loggedIn,
      main,
      mobileBottomNav,
      mobileTopNav,
      navbarPosition,
      routeParams,
      setRouteEnd,
      showMobileBackground,
      shouldShowWebDiscountBanner,
    ]
  );

  return (
    <PreRouteCheck
      currentPath={currentPath}
      hasPrevPath={hasPrevPath}
      loading={loading}
      loggedIn={loggedIn}
      onEnter={routeConfig?.onEnter}
      params={routeParams}
      prefetch={routeConfig?.prefetch}
      search={search}
    >
      {mainNode}
      <PageTitle {...pageMeta} />
    </PreRouteCheck>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(Mobile, areEqual));
